import Vue from "vue";
import VueI18n from "vue-i18n";
import axios from "axios";
import {avalibleLanguages} from "@/utils/i18nUtils"

Vue.use(VueI18n);

/**
 * Recorre recursivamente un objeto.
 * Si encuentra claves con puntos (ej: "a.b"), las convierte en objetos anidados.
 * Respeta la estructura existente y fusiona los resultados.
 */
function deepUnflatten(data) {
  // 1. Caso base: Si no es un objeto (es string, null, etc.), lo devuelve tal cual
  if (Object(data) !== data || Array.isArray(data)) {
    return data;
  }

  const result = {};

  for (const key in data) {
    // 2. RECURSIVIDAD: Procesamos primero el valor de la clave.
    // Esto asegura que si estamos en "general", procesemos su contenido antes de asignarlo.
    const value = deepUnflatten(data[key]);

    // 3. Lógica de expansión de claves con puntos
    if (key.indexOf('.') !== -1) {
      const parts = key.split('.');
      let current = result;

      parts.forEach((part, index) => {
        // Si es el último fragmento de la clave (ej: "noModel" en "initMessages.noModel")
        if (index === parts.length - 1) {
          current[part] = value;
        } else {
          // Si no existe el nivel intermedio, lo creamos.
          // Si ya existe (porque otra clave lo creó), lo reutilizamos.
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
      });
    } else {
      // 4. Si la clave NO tiene puntos (ej: "general" o claves normales)
      // Verificamos si ya existe algo en 'result' con ese nombre (por fusión de claves anteriores)
      if (result[key] && typeof result[key] === 'object') {
        // Si ya existe y es un objeto, mezclamos lo nuevo con lo viejo
        Object.assign(result[key], value);
      } else {
        // Si no, asignamos directamente
        result[key] = value;
      }
    }
  }

  return result;
}

function getAvailableLocales() {
  try {
    const locales = require.context('./locales', true, /[A-Za-z0-9-_,\s]+\.json$/i);
    // Buscar archivos con el patrón ./locales/general/general.xx.json
    const availableLocales = locales.keys()
      .map(file => {
        // Extraer el código de idioma del nombre del archivo
        // Ejemplo: "./general/general.es.json" -> "es"
        const match = file.match(/\.\/general\/general\.([a-zA-Z0-9-]+)\.json$/);
        return match ? match[1] : null;
      })
      .filter(Boolean);
    return availableLocales.filter(locale => avalibleLanguages.some(lang => lang.code === locale));
  } catch (error) {
    console.error("Failed to get available locales", error);
    return [];
  }
}

function loadLocalMessages(locale) {
  try {
    const locales = require.context('./locales', true, /[A-Za-z0-9-_,\s]+\.json$/i);
    return locales(`./${locale}.json`);
  } catch (error) {
    console.error("Failed to load remote locale", error);
    return null;
  }
}

function loadLocalDirMessages(locale) {
  try {
    // Obtener todos los archivos JSON de las subcarpetas en ./locales
    const locales = require.context('./locales', true, /[A-Za-z0-9-_,\s]+\.json$/i);
    const result = {};

    // Obtener todas las rutas de los archivos
    const keys = locales.keys();

    keys.forEach(key => {
      // Extraer el nombre de la carpeta y el locale del path
      // Ejemplo: "./general/general.es.json" -> ["general", "general.es.json"]
      const pathParts = key.replace('./', '').split('/');

      if (pathParts.length >= 2) {
        const folderName = pathParts[0];
        const fileName = pathParts[pathParts.length - 1];

        // Verificar si el archivo corresponde al locale solicitado
        // Ejemplo: "general.es.json" debe contener ".es."
        if (fileName.includes(`.${locale}.`)) {
          try {
            const messages = locales(key);
            result[folderName] = messages;
          } catch (loadError) {
            console.warn(`Failed to load messages from ${key}:`, loadError);
          }
        }
      }
    });

    return Object.keys(result).length > 0 ? result : null;
  } catch (error) {
    console.error("Failed to load locale messages", error);
    return null;
  }
}

async function loadRemoteMessages(locale) {
  try {
    const blob = process.env.VUE_APP_BLOB_URL;
    const response = await axios.get(`${process.env.VUE_APP_STATIC_IMG_URL}/${blob}/locale/${locale}.json`);
    return response.data;
  } catch (error) {
    console.error("Failed to load remote locale", error);
    throw error;
  }
}

const i18n = new VueI18n({
  locale: process.env.VUE_APP_I18N_LOCALE || "en",
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || "en",
  messages: {},
});

i18n.loadedLanguages = {};
i18n.customAvailableLocales = getAvailableLocales();
i18n.extraInfoAvailableLocales = avalibleLanguages;

i18n.loadLanguageAsync = async (locale) => {
  const loadMessages = async (lang) => {
    if (i18n.loadedLanguages[lang]) {
      i18n.setLocaleMessage(lang, i18n.loadedLanguages[lang]);
      return;
    }

    const localesContext = require.context('./locales', true, /[A-Za-z0-9-_,\s]+\.json$/i);
    const hasFolders = localesContext.keys().some(key => key.replace('./', '').includes('/'));

    let localMessages = hasFolders
      ? loadLocalDirMessages(lang) || {}
      : loadLocalMessages(lang) || {};

    let remoteMessages = [];
    try {
      remoteMessages = await loadRemoteMessages(lang);
    } catch (error) {
      console.error("Failed to load remote locale", error);
    }

    // 1. Fusionamos los mensajes tal cual vienen
    const rawCombinedMessages = { ...localMessages, ...remoteMessages };

    // 2. APLICAMOS LA FUNCIÓN UNFLATTEN AQUÍ
    // Esto transformará "initMessages.noModel": "X" en { initMessages: { noModel: "X" } }
    const processedMessages = deepUnflatten(rawCombinedMessages);

    // 3. Guardamos y establecemos los mensajes procesados
    i18n.loadedLanguages[lang] = processedMessages;
    i18n.setLocaleMessage(lang, processedMessages);
  };

  const fallback = process.env.VUE_APP_I18N_FALLBACK_LOCALE || "en";
  if (!locale) locale = fallback;

  await loadMessages(locale);
  i18n.locale = locale;

  if (locale !== fallback) {
    await loadMessages(fallback);
  }
}


export default i18n;
